import { CodeXml } from 'lucide-react';
import type { TestScriptFileRow } from '@/components/smartai/api/getTestScriptFiles';
import { cn } from '@/lib/utils';
import {
  formatScriptIndexedAt,
  inferScriptFramework,
  listScriptFilesFromRows,
  scriptSuiteLabel,
  type ScriptFileNode,
} from './scriptPathTree';
import {
  testCasesEmptyMessageClass,
  testCasesScriptCardClass,
  testCasesScriptCardSelectedClass,
  testCasesScriptIconWrapClass,
  testCasesScriptMainClass,
  testCasesScriptMetaClass,
  testCasesScriptNameClass,
  testCasesScriptCoverageClass,
  testCasesScriptCoverageLabelClass,
  testCasesScriptsListClass,
} from './test-cases-layout';

export type ScriptCoverageSummary = {
  pct: number;
  hit: number;
  total: number;
};

type ScriptInventoryListProps = {
  files: TestScriptFileRow[];
  repoName?: string;
  selectedPathKey?: string | null;
  coverageByPath?: Record<string, ScriptCoverageSummary>;
  onSelect: (node: ScriptFileNode) => void;
};


const ScriptInventoryList = ({
  files,
  repoName,
  selectedPathKey,
  coverageByPath = {},
  onSelect,
}: ScriptInventoryListProps) => {
  const scriptFiles = listScriptFilesFromRows(files);

  if (scriptFiles.length === 0) {
    return (
      <p className={testCasesEmptyMessageClass}>
        No indexed test scripts for this inventory.
      </p>
    );
  }

  const rowByPath = new Map(
    files
      .map(row => [String(row.filePath ?? '').trim(), row] as const)
      .filter(([path]) => path.length > 0),
  );

  return (
    <div className={testCasesScriptsListClass}>
      {scriptFiles.map(node => {
        const row = rowByPath.get(node.pathKey);
        const coverage = coverageByPath[node.pathKey];
        const selected = selectedPathKey === node.pathKey;
        const coveredLabel =
          coverage && coverage.total > 0
            ? { count: coverage.hit, label: 'covered' }
            : row?.status === 'removed'
              ? { count: null, label: 'removed' }
              : null;

        return (
          <button
            key={node.pathKey}
            type="button"
            className={cn(
              testCasesScriptCardClass,
              'w-full items-center text-left',
              selected && testCasesScriptCardSelectedClass,
            )}
            aria-current={selected ? 'true' : undefined}
            onClick={() => onSelect(node)}>
            <div className={testCasesScriptIconWrapClass}>
              <CodeXml className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            </div>

            <div className={testCasesScriptMainClass}>
              <p className={testCasesScriptNameClass}>{node.fileName}</p>
              <p className={testCasesScriptMetaClass}>
                {/* {scriptSuiteLabel(node.filePath)}
                {' · '} */}
                {repoName || 'Repository'}
                {' · '}
                {inferScriptFramework(node.fileName)}
                {/* {' · '}
                Indexed: {formatScriptIndexedAt(row?.createdAt)} */}
              </p>
            </div>

            {coveredLabel ? (
              <div
                className={cn(
                  testCasesScriptCoverageClass,
                  'ml-auto self-center',
                )}>
                {coveredLabel.count != null ? (
                  <div className="text-[1.375rem] font-bold leading-none tabular-nums text-emerald-600">
                    {coveredLabel.count}
                  </div>
                ) : null}
                <div
                  className={cn(
                    testCasesScriptCoverageLabelClass,
                    coveredLabel.count == null && 'normal-case text-red-600',
                  )}>
                  {coveredLabel.label}
                </div>
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default ScriptInventoryList;
